import { Button, Col, Drawer, Form, Input, Row, Select, Space, Switch, message } from 'antd';
import axios from 'axios';
import { apiBasePath } from '../../../utils/constants';
import { useEffect, useState } from 'react';
import { allowOnlyNumbers, valueToUpperCase, withTrimmedForm } from '../../../utils/utils';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { CifHelpModal } from '../../login/cifHelpModal';

export const ClientForm = ({ open, selectedClient, onClose, setWasEdited }) => {
  const isEdit = !!selectedClient;
  const [messageApi, contextHolder] = message.useMessage();
  const [loader, setLoader] = useState(false);
  const [CFDIuse, setCFDIuse] = useState([]);
  const [form] = Form.useForm();
  const [tipoPersona, setTipoPersona] = useState(isEdit ? selectedClient?.clie_tipo : 1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [campoActivo, setCampoActivo] = useState(null);

  const renderLabelWithHelp = (label, fieldKey) => (
    <span>
      {label}{' '}
      <QuestionCircleOutlined
        className="icono-ayuda-input"
        onClick={(e) => {
          e.stopPropagation();
          setCampoActivo(fieldKey);
          setIsModalOpen(true);
        }}
      />
    </span>
  );

  const getCFDIuser = async () => {
    try {
      const response = await axios.get(`${apiBasePath}/reference-data/tax-regime`);
      const useCFDI = response.data?.taxRegime?.data || [];
      setCFDIuse(useCFDI);
    } catch (error) {
      console.error('🚀 ~ getCFDIuser ~ error:', error);
    }
  };

  useEffect(() => {
    if (isEdit) {
      setTipoPersona(selectedClient?.clie_tipo);
      form.setFieldsValue(selectedClient);
    } else {
      setTipoPersona(1);
      form.resetFields();
    }
    getCFDIuser();
  }, [selectedClient?.clie_id]);

  const onFinishForm = async (values) => {
    setLoader(true);
    try {
      await axios({
        method: isEdit ? 'PUT' : 'POST',
        url: `${apiBasePath}/customer`,
        data: {
          ...values,
          clie_id: isEdit ? selectedClient?.clie_id : null,
          clie_tel: values.clie_tel ? values.clie_tel : null,
          clie_password: values.clie_rfc,
          clie_colonia_id: 0,
          clie_activo: values.clie_activo ? 1 : 0
        }
      });
      await messageApi.open({
        type: 'success',
        content: isEdit ? 'Actualización exitosa' : 'Registro exitoso'
      });
      setWasEdited.current = true;
      form.resetFields();
      onClose();
    } catch (error) {
      const messageError = error.response?.data?.message;
      const msgshowError = messageError
        ? messageError
        : isEdit
          ? 'Tuvimos problemas al actualizar tu cuenta'
          : 'Tuvimos problemas al registrar tu cuenta';
      messageApi.open({
        type: 'error',
        content: msgshowError,
        description: 'Por favor, intenta de nuevo'
      });
    } finally {
      setLoader(false);
    }
  };
  return (
    <Drawer
      title={isEdit ? 'Editar Cliente' : 'Agregar Cliente'}
      size={840}
      width={840}
      open={open}
      onClose={onClose}
      styles={{
        body: {
          paddingBottom: 80
        }
      }}
      extra={
        <Space>
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="primary" loading={loader} onClick={form.submit}>
            Guardar
          </Button>
        </Space>
      }>
      {contextHolder}
      <Form
        name="login-register"
        form={form}
        layout="vertical"
        size="small"
        onFinish={withTrimmedForm(onFinishForm)}
        autoComplete="off"
        className="login-register-form">
        <div className="register-inputs-container">
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="clie_tipo"
                label="Tipo de persona"
                rules={[{ required: true, message: 'Por favor ingresa el tipo de persona' }]}>
                <Select onChange={(value) => setTipoPersona(value)}>
                  <Select.Option value={1}>Persona Física</Select.Option>
                  <Select.Option value={2}>Persona Moral</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="clie_nombre"
                label={tipoPersona === 1 ? 'Nombre' : renderLabelWithHelp('Razón social', 'razon_social')}
                onKeyUp={(e) => valueToUpperCase(e.target.value, form, 'clie_nombre')}
                rules={[{ required: true, message: 'Por favor ingresa tu nombre' }]}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
          {tipoPersona === 1 && (
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="clie_ap"
                  label="Apellido paterno"
                  onKeyUp={(e) => valueToUpperCase(e.target.value, form, 'clie_ap')}
                  rules={[{ required: true, message: 'Por favor ingresa tu apellidos paterno' }]}>
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="clie_am"
                  label="Apellido materno"
                  onKeyUp={(e) => valueToUpperCase(e.target.value, form, 'clie_am')}
                  rules={[{ required: true, message: 'Por favor ingresa tu apellido materno' }]}>
                  <Input />
                </Form.Item>
              </Col>
            </Row>
          )}
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="clie_cp"
                label={renderLabelWithHelp('Código Postal', 'codigo_postal')}
                onKeyDown={(e) => allowOnlyNumbers(e, true)}
                rules={[
                  { required: true, message: 'Por favor ingresa tu codigo postal' },
                  {
                    max: 5,
                    min: 5,
                    message: 'Por favor ingresa un Código Postal valido'
                  }
                ]}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="clie_tel"
                label="Teléfono"
                onKeyDown={(e) => allowOnlyNumbers(e, true)}
                rules={[
                  {
                    min: 10,
                    message: 'Por favor ingresa un teléfono valido'
                  }
                ]}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="clie_rfc"
                label={renderLabelWithHelp('RFC', 'rfc')}
                onKeyUp={(e) => valueToUpperCase(e.target.value, form, 'clie_rfc')}
                rules={[
                  { required: true, message: 'Por favor ingresa tu RFC' },
                  {
                    max: 13,
                    message: 'Por favor ingresa un RFC valido'
                  }
                ]}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="clie_regimen_fiscal"
                label={renderLabelWithHelp('Régimen Fiscal', 'regimen_fiscal')}
                rules={[{ required: true, message: 'Por favor ingresa tu regimen fiscal' }]}>
                <Select
                  showSearch
                  filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}>
                  {CFDIuse.map((item) => (
                    <Select.Option key={`cfdi-${item.id}`} value={item.code} label={`${item.code}-${item.description}`}>
                      {`${item.code}-${item.description}`}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item
                name="clie_correo"
                label="Correo electrónico"
                rules={[
                  { required: true, message: 'Por favor ingresa tu correo electrónico' },
                  { type: 'email', message: 'Por favor ingresa un correo electrónico valido' }
                ]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Activo" name="clie_activo">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </div>
      </Form>
      <CifHelpModal 
        isModalOpen={isModalOpen} 
        setIsModalOpen={setIsModalOpen} 
        campoActivo={campoActivo} 
      />
    </Drawer>
  );
};
